import { apiClient } from "./apiClient";

export interface DLQMetrics {
  dlq_length: number;
  recent_entries_count: number;
  spike_detected: boolean;
  error_categories: Record<string, number>;
  spike_threshold: number;
}

export interface DLQUserInfo {
  user_id: string;
  email: string;
  name: string;
  status: string;
}

export interface DLQEntry {
  job_id: string;
  job_type: string;
  user_id: string;
  user_info: DLQUserInfo | null;
  signal_id: string | null;
  position_id: string | null;
  recommendation_id: string | null;
  symbol: string | null;
  attempts: number;
  max_attempts: number;
  error_category: string | null;
  last_error: string | null;
  last_error_code: string | null;
  last_error_message: string | null;
  dead_letter_reason: string | null;
  job_payload: any;
  created_at: string | null;
  completed_at: string | null;
}

export interface DLQEntriesResponse {
  entries: DLQEntry[];
  count: number;
  total_count: number;
  limit: number;
  offset: number;
}

export async function getDLQMetrics(): Promise<DLQMetrics> {
  try {
    const response = await apiClient.get<DLQMetrics>("/admin/dlq/metrics");
    return response.data;
  } catch (error: any) {
    const errorMessage = error?.response?.data?.detail || error?.message || "Failed to fetch DLQ metrics";
    throw new Error(errorMessage);
  }
}

export async function getDLQEntries(limit: number = 100, offset: number = 0): Promise<DLQEntriesResponse> {
  try {
    const response = await apiClient.get<DLQEntriesResponse>("/admin/dlq/entries", {
      params: { limit, offset },
    });
    return response.data;
  } catch (error: any) {
    const errorMessage = error?.response?.data?.detail || error?.message || "Failed to fetch DLQ entries";
    throw new Error(errorMessage);
  }
}

export async function retryDLQEntry(entryId: string): Promise<{ status: string; message: string; new_job_id?: string }> {
  try {
    const response = await apiClient.post<{ status: string; message: string; new_job_id?: string }>(
      `/admin/dlq/entries/${entryId}/retry`
    );
    return response.data;
  } catch (error: any) {
    const errorMessage = error?.response?.data?.detail || error?.message || "Failed to retry DLQ entry";
    throw new Error(errorMessage);
  }
}

export async function deleteDLQEntry(entryId: string): Promise<{ status: string; message: string }> {
  try {
    const response = await apiClient.delete<{ status: string; message: string }>(
      `/admin/dlq/entries/${entryId}`
    );
    return response.data;
  } catch (error: any) {
    const errorMessage = error?.response?.data?.detail || error?.message || "Failed to delete DLQ entry";
    throw new Error(errorMessage);
  }
}

export interface DeleteAllDLQResponse {
  status: string;
  message: string;
  deleted_from_db: number;
  deleted_from_redis: number;
  errors: string[];
}

export async function deleteAllDLQEntries(): Promise<DeleteAllDLQResponse> {
  try {
    const response = await apiClient.delete<DeleteAllDLQResponse>("/admin/dlq/entries/all");
    return response.data;
  } catch (error: any) {
    const errorMessage = error?.response?.data?.detail || error?.message || "Failed to delete all DLQ entries";
    throw new Error(errorMessage);
  }
}

